import React, {useEffect, useMemo, useRef, useState} from "react";
import _ from "lodash";
import {useAtomValue} from "jotai";
import * as turf from "@turf/turf";
import {MultiPolygon} from "geojson";
import {default as GLMap, MapRef} from "react-map-gl";
import {useTranslation} from "react-i18next";
import {DrawModeChangeEvent} from "@mapbox/mapbox-gl-draw";
import MapboxDraw, {MapboxDrawOptions} from "@mapbox/mapbox-gl-draw";
import CenterLocationIcon from "@mui/icons-material/GpsNotFixed";
import {Box, Button, Paper, Stack, Typography} from "@mui/material";
import {BackHand, OpenWith, RemoveCircleOutline} from "@mui/icons-material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import {mapTokenLoadable} from "../../store/mapTokenStore";
import {mapDrawStyles} from "./FieldDrawMapStyles";
import {RemoveVertexMode, StaticMode} from "./FieldDrawMapCustomModes";
import {calcAreaHaGeometry} from "../../utils/mapUtils";
import {fitMapToBbox} from "../../utils/mapboxUtils";
import {MapSlider} from "./MapSlider";
import {FieldsShapeFileUploadForm} from "../fields/FieldsShapeFileUploadForm";
import {Field} from "../../types/domain/field";
import {useMapboxGeolocator} from "../../hooks/mapboxHooks";
import {StyledButton, ToggleButtons} from "./MapDrawToolButtons";
import {useMeasureSystemUtils} from "../../hooks/measureSystemUtilsHooks";

const modes = MapboxDraw.constants.modes;
const MOVE_BUTTON_ID = "move-btn-id";
const ADD_BUTTON_ID = "add-btn-id";
const ADJUST_BUTTON_ID = "adjust-btn-id";
const REMOVE_BUTTON_ID = "remove-btn-id";

interface FieldDrawMapProps {
    setGeometry: (polygon: MultiPolygon | null) => void;
    geometry: MultiPolygon | null;
    setFields: (fields: Field[]) => void;
    mode: "new" | "edit";
}

export const FieldDrawMap: React.FC<FieldDrawMapProps> = ({setGeometry, geometry, setFields, mode}) => {
    const {t} = useTranslation();
    const msUtils = useMeasureSystemUtils();
    const [history, setHistory] = useState<MultiPolygon[]>([]);
    const moveToButton = new Map<string, string>();
    const [activeButtonId, setActiveButtonId] = useState(MOVE_BUTTON_ID);
    const [zoom, setZoom] = useState<number>(4);
    const mapRef = useRef<MapRef>(null);
    const geocoderContainerRef = useRef<HTMLDivElement | null>(null);
    const [areaHa, setAreaHa] = useState(0);
    const mapToken = useAtomValue(mapTokenLoadable);

    moveToButton.set(modes.STATIC, MOVE_BUTTON_ID);
    moveToButton.set(modes.DRAW_POLYGON, ADD_BUTTON_ID);
    moveToButton.set(modes.DIRECT_SELECT, ADJUST_BUTTON_ID);

    const realignMap = (_geometry: MultiPolygon | null) => {
        if (_geometry) {
            const mapFitBounds = turf.bbox(_geometry);
            if (mapRef.current && mapFitBounds) {
                fitMapToBbox(mapRef.current, mapFitBounds);
            }
        }
    };

    const addToHistory = (_geometry: MultiPolygon | null) => {
        if (_geometry) {
            if (!_.isEqual(history[history.length - 1], _geometry)) {
                const _history = [...history, _geometry];
                setHistory(_history);
            }
        }
    };

    const pickFromHistory = () => {
        if (history.length !== 0) {
            const newHistory = [...history];
            const geom = newHistory.pop()!;
            mapboxDraw.deleteAll();
            if (geom) {
                mapboxDraw.add(geom);
            }
            setGeometry(geom);
            setHistory(newHistory);
            updateArea(geom);
        }
    };

    //Event handlers
    const onZoomChange = (value: number) => {
        if (mapRef.current) {
            mapRef.current.setZoom(value);
        }
    };

    const onModeChange = (mode: string) => {
        if (mode === modes.SIMPLE_SELECT) {
            mapboxDraw.changeMode(modes.STATIC);
        }
        let buttonID = MOVE_BUTTON_ID;
        if (moveToButton.has(mode)) {
            buttonID = moveToButton.get(mode)!;
        }
        setActiveButtonId(buttonID);
    };

    const mapboxDraw = useMemo(() => {
        const options: MapboxDrawOptions = {};
        const modesImpl = MapboxDraw.modes;
        options.displayControlsDefault = false;
        options.userProperties = true;
        options.controls = {
            polygon: false,
            trash: false,
        };
        options.styles = mapDrawStyles;
        const mapboxDraw = new MapboxDraw(options);

        MapboxDraw.modes = modesImpl;
        MapboxDraw.modes.remove_vertex = RemoveVertexMode(mapboxDraw, modesImpl.direct_select, onModeChange);
        MapboxDraw.modes.static = StaticMode;
        return mapboxDraw;
        /* eslint-disable react-hooks/exhaustive-deps */
    }, []);

    const redrawMap = (_geometry: MultiPolygon | null) => {
        mapboxDraw.deleteAll();
        if (_geometry) {
            mapboxDraw.add(_geometry);
        }
    };

    const updateArea = (_geometry: MultiPolygon | null) => {
        if (_geometry) {
            const _area = calcAreaHaGeometry(_geometry);
            setAreaHa(msUtils.getArea(_area));
        } else {
            setAreaHa(0);
        }
    };

    const handleMoveMode = () => {
        mapboxDraw.changeMode(modes.STATIC);
    };

    const handleAddPoint = () => {
        if (mapboxDraw.getAll().features.length === 0) {
            // If there are no existing polygons, switch to draw_polygon mode
            mapboxDraw.changeMode(modes.DRAW_POLYGON);
        } else {
            // If there is an existing polygon, switch to simple_select mode to allow adding points
            const feature = mapboxDraw.getAll().features[0];
            if (feature.id) {
                const featureId = feature.id as string;
                mapboxDraw.changeMode(modes.DIRECT_SELECT, {featureId});
                onModeChange(modes.DIRECT_SELECT);
            }
        }
    };

    const handleAdjustPoint = () => {
        if (mapboxDraw.getAll().features.length > 0) {
            const feature = mapboxDraw.getAll().features[0];
            if (feature.id) {
                const featureId = feature.id as string;
                mapboxDraw.changeMode(modes.DIRECT_SELECT, {featureId});
            }
        }
    };

    const handleRemovePoint = () => {
        const features = mapboxDraw.getAll().features;
        if (features && features.length > 0) {
            const feature = mapboxDraw.getAll().features[0];
            const featureId = feature.id as string;
            mapboxDraw.changeMode("remove_vertex", {featureId});
        }
    };
    const handleRemoveShape = () => {
        const features = mapboxDraw.getAll().features;
        if (features && features.length > 0) {
            const geometry = features[0].geometry as MultiPolygon;
            addToHistory(geometry);
        }
        mapboxDraw.deleteAll();
        setGeometry(null);
        updateArea(null);
    };
    const [mapLoaded, setMapLoaded] = useState(false);

    const mapboxGeolocator: MapboxGeocoder | null = useMapboxGeolocator(zoom, t);
    useEffect(() => {
        if (mapboxGeolocator && mapToken.state === "hasData") {
            mapboxGeolocator.setAccessToken(mapToken.data);
        }
        /* eslint-disable react-hooks/exhaustive-deps */
    }, [mapToken.state, mapboxGeolocator]);

    useEffect(() => {
        if (mapRef !== null && mapRef !== undefined && mapRef.current) {
            const map = mapRef.current.getMap();
            if (!map.hasControl(mapboxDraw)) {
                map.addControl(mapboxDraw, "top-left");
                //If you programmatically invoke a function in the Draw API, any event that directly corresponds with that function will not be fired.
                //Meaning if you programmatically call draw.delete(), the on("draw.delete") handler won't be called.
                // https://github.com/mapbox/mapbox-gl-draw/blob/main/docs/API.md#events
                const updateFeatures = () => {
                    const features = mapboxDraw.getAll().features;
                    if (features && features.length > 0) {
                        const geometry = features[0].geometry as MultiPolygon;
                        const newGeometry = _.cloneDeep(geometry);
                        setGeometry(newGeometry);
                        updateArea(newGeometry);
                    }
                };
                map.on("draw.create", updateFeatures);
                map.on("draw.update", updateFeatures);
                map.on("draw.delete", updateFeatures);
                map.on("draw.modechange", (e) => {
                    onModeChange((e as DrawModeChangeEvent).mode);
                });
                redrawMap(geometry);
            }
            if (geometry) {
                updateArea(geometry);
            }
        }
        /* eslint-disable react-hooks/exhaustive-deps */
    }, [mapLoaded, geometry, setGeometry, mapboxDraw]);

    useEffect(() => {
        if (mapRef !== null && mapRef !== undefined && mapRef.current && mapboxGeolocator) {
            const map = mapRef.current.getMap();
            if (geocoderContainerRef.current) {
                geocoderContainerRef.current.appendChild(mapboxGeolocator.onAdd(map));
            }

            //Need to reset mapboxgl-ctrl style
            const geocoder: HTMLElement = document.querySelector(".mapboxgl-ctrl-geocoder")!;
            if (geocoder) {
                geocoder.style.width = "100%";
                geocoder.style.maxWidth = "100%";
            }
            return () => {
                mapboxGeolocator.onRemove(); // Clean up the geocoder control
            };
        } else {
            /* eslint-disable @typescript-eslint/no-empty-function */
            return () => {};
        }
    }, [mapLoaded, mapboxGeolocator]);

    useEffect(() => {
        if (mapRef !== null && mapRef !== undefined && mapRef.current) {
            if (geometry) {
                //TODO investigate this
                //This exceptions, happens when the mapboxDraw is initialized but not rendered completely, some of the internal methods is failing
                try {
                    redrawMap(geometry);
                    realignMap(geometry);
                } catch {
                    //no op
                }
            }
        }

        //We don't want to redraw map on the geometry change.
        /* eslint-disable react-hooks/exhaustive-deps */
    }, [mapRef.current, mapboxDraw]);

    useEffect(() => {
        if (mapRef.current) {
            realignMap(geometry);
        }
    }, [mapRef.current]);

    const translationBase = "fields.field_draw_map";
    const buttons = [
        {
            id: MOVE_BUTTON_ID,
            label: t(`${translationBase}.buttons.move_tool`),
            icon: BackHand,
            onClick: handleMoveMode,
        },
        {
            id: ADD_BUTTON_ID,
            label: t(`${translationBase}.buttons.add_points`),
            icon: AddCircleOutlineIcon,
            onClick: handleAddPoint,
        },
        {
            id: ADJUST_BUTTON_ID,
            label: t(`${translationBase}.buttons.adjust_points`),
            icon: OpenWith,
            onClick: handleAdjustPoint,
        },
        {
            id: REMOVE_BUTTON_ID,
            label: t(`${translationBase}.buttons.remove_points`),
            icon: RemoveCircleOutline,
            onClick: handleRemovePoint,
        },
    ];

    return (
        <Stack sx={{height: "100%", width: "100%", position: "relative"}}>
            {mapToken.state === "hasData" && (
                <>
                    <GLMap
                        style={{minHeight: 800}}
                        ref={mapRef}
                        onZoom={(e) => {
                            setZoom(e.viewState.zoom);
                        }}
                        mapStyle="mapbox://styles/mapbox/satellite-v9"
                        mapboxAccessToken={mapToken.data}
                        onLoad={() => setMapLoaded(true)}
                        interactiveLayerIds={["fill-layer"]}
                    ></GLMap>
                </>
            )}

            <div
                id="geocoder-container"
                ref={geocoderContainerRef}
                style={{
                    width: "calc(100% - 3rem)",
                    position: "absolute",
                    top: 0,
                    left: 0,
                    marginTop: "1.5rem",
                    marginLeft: "1.5rem",
                }}
            ></div>
            <Stack
                sx={{width: "240px", position: "absolute", top: 0, left: 0, marginTop: "5rem", marginLeft: "1.5rem"}}
            >
                <Stack component={Paper} direction="column" gap="1rem" sx={{padding: "0.75rem"}}>
                    <Typography variant="h5">{t(`${translationBase}.header`)}</Typography>
                    <Typography>{t(`${translationBase}.instructions`)}</Typography>
                    <Box>
                        <Typography variant="h6">{t(`${translationBase}.calculated_area`)}</Typography>
                        <Typography sx={{fontSize: "1rem", fontWeight: 500, color: "##474C5E"}}>
                            {Number(areaHa).toFixed(2)} {msUtils.getAreaUnits()}
                        </Typography>
                    </Box>
                    <Stack direction="column" gap="0.75rem">
                        <Box>
                            <Typography variant="h6" sx={{paddingBottom: "6px"}}>
                                {t(`${translationBase}.tools`)}
                            </Typography>
                            <ToggleButtons
                                activeButtonId={activeButtonId}
                                setActiveButtonId={setActiveButtonId}
                                buttons={buttons}
                            ></ToggleButtons>
                        </Box>

                        <Box>
                            {mode === "new" && (
                                <FieldsShapeFileUploadForm
                                    setFields={(value) => {
                                        setFields(value);
                                        if (value.length > 0) {
                                            const _geometry = value[0].geometry;
                                            redrawMap(_geometry);
                                            setGeometry(_geometry);
                                            realignMap(_geometry);
                                        }
                                    }}
                                />
                            )}
                            <StyledButton
                                key={"delete-btn"}
                                variant={"outlined"}
                                color={"secondary"}
                                startIcon={<DeleteOutlinedIcon sx={{color: "#d32f2f"}} />}
                                onClick={handleRemoveShape}
                                style={{
                                    justifyContent: "flex-start",
                                    width: "100%",
                                    borderRadius: "0px",
                                    backgroundColor: "#DBDEE6",
                                    color: "#2B303F",
                                }}
                            >
                                {t(`${translationBase}.buttons.delete_shape`)}
                            </StyledButton>
                            <StyledButton
                                key={"undo-btn"}
                                variant={"outlined"}
                                color={"secondary"}
                                startIcon={<UndoOutlinedIcon />}
                                onClick={pickFromHistory}
                                disabled={history.length === 0}
                                style={{
                                    justifyContent: "flex-start",
                                    width: "100%",
                                    borderRadius: "0px",
                                    backgroundColor: "#DBDEE6",
                                    color: "#2B303F",
                                    borderWidth: "0px",
                                }}
                            >
                                {t(`${translationBase}.buttons.undo`)}
                            </StyledButton>
                        </Box>
                    </Stack>
                </Stack>
            </Stack>
            <Box sx={{position: "absolute", top: 0, right: 0, marginTop: "5rem", marginRight: "2rem"}}>
                <Stack direction="column" gap="1rem">
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => {
                            realignMap(geometry);
                        }}
                        startIcon={<CenterLocationIcon />}
                        sx={{
                            "& .MuiButton-startIcon": {margin: 0},
                            minWidth: 0,
                            padding: "0.5rem",
                            width: "2rem",
                            height: "2rem",
                        }}
                    />

                    <MapSlider
                        zoom={zoom}
                        onZoomChange={(value) => {
                            onZoomChange(value);
                        }}
                    />
                </Stack>
            </Box>
        </Stack>
    );
};
